/**
 * Shared with StudyWorkspaceHubPage.tsx (hide/show the "Manage staff"
 * entry points) and AssignStaffDialog.tsx (gate the actual submit) so
 * both agree on the same rule — mirrors the scoped check in the backend's
 * staffAssign.ts / staffRevoke.ts.
 */

/**
 * PI, SI and Coordinator assign staff on studies where they hold one of those
 * roles themselves — the role ON the study, so a Site User delegated as
 * Coordinator can (Harish, 1 Oct 2026).
 */
export const STUDY_STAFF_MANAGER_ROLES = new Set(['principal_investigator', 'sub_investigator', 'coordinator']);

export interface StudyDelegationLike {
  user_id: string;
  role: string;
  /** False when deactivated or removed from the site: no longer running the study. */
  active?: boolean;
}

/** Sign-in roles that onboard a study — and pick its team — until it is activated (Harish, 2 Oct 2026). */
export const STUDY_ONBOARDING_ROLES = new Set([
  'principal_investigator', 'sub_investigator', 'coordinator', 'site_study_designer', 'site_admin',
]);

/** Sign-in roles that add and remove a study's staff at any stage (Harish, 7 Oct 2026). */
export const SITE_STAFFING_ROLES = new Set(['site_admin', 'site_study_designer']);

export function canManageStudyStaff(
  userRole: string | undefined | null,
  userId: string | undefined | null,
  studyDelegations: readonly StudyDelegationLike[],
  activated = true,
): boolean {
  if (!userRole || !userId) return false;
  if (SITE_STAFFING_ROLES.has(userRole)) return true;
  // Before activation any onboarder picks the team, on the team or not.
  if (!activated && STUDY_ONBOARDING_ROLES.has(userRole)) return true;
  // The platform admins never assign study staff.
  return studyDelegations.some((d) => d.user_id === userId && STUDY_STAFF_MANAGER_ROLES.has(d.role));
}
